import {AriaRole, CSSProperties, ReactNode} from 'react';

import {suffixTestId, TestIdProps} from 'shared/web';

import {
  FLEX_ALIGN_CLASSES,
  FLEX_DIRECTION_CLASSES,
  FLEX_JUSTIFY_CLASSES,
} from '../../class-maps/flexClasses';
import {FLEX_ITEM_STYLE_MAPPINGS, SIZING_STYLE_MAPPINGS} from '../../class-maps/styleMappings';
import {CSSDimension} from '../../types/CSSDimension';
import {Integer} from '../../types/Integer';
import {ResponsiveValue} from '../../types/ResponsiveValue';
import {buildStylesFromSchema} from '../../utils/buildStylesFromSchema';
import {cn} from '../../utils/cn';
import {getResponsiveStaticClass} from '../../utils/getSpacingStyle';
import {resolveSpacingValue} from '../../utils/resolveSpacingValue';

type FlexboxDirection = 'row' | 'column' | 'row-reverse' | 'column-reverse';
type FlexboxAlign = 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
type FlexboxJustify =
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'space-between'
  | 'space-around'
  | 'space-evenly';

export interface StackProps extends TestIdProps {
  children: ReactNode;

  // Flexbox direction
  direction: FlexboxDirection | ResponsiveValue<FlexboxDirection>;

  // Accessibility
  role?: AriaRole;

  // Spacing (gap)
  spacing?: Integer | ResponsiveValue<Integer>;

  // Alignment
  align?: FlexboxAlign | ResponsiveValue<FlexboxAlign>;
  justify?: FlexboxJustify | ResponsiveValue<FlexboxJustify>;

  // Wrap
  wrap?: boolean | 'nowrap';

  // Flex item props
  grow?: number;
  shrink?: number;
  basis?: string;

  // Sizing
  width?: Integer | CSSDimension;
  height?: Integer | CSSDimension;
  maxWidth?: Integer | CSSDimension;
  maxHeight?: Integer | CSSDimension;
  minWidth?: Integer | CSSDimension;
  minHeight?: Integer | CSSDimension;
}

/**
 * Converts StackProps to Record for schema builder
 */
const toRecord = (props: StackProps): Record<string, unknown> =>
  Object.fromEntries(Object.entries(props));

/**
 * Stack component - flexible flexbox container
 *
 * Spacing (gap) is always applied via inline styles for simplicity and flexibility.
 *
 * @example
 * <Stack direction="column" spacing={4}>
 *   <div>Item 1</div>
 *   <div>Item 2</div>
 * </Stack>
 * <Stack direction={{ base: 'column', lg: 'row' }} spacing={{ base: 2, lg: 4 }}>
 *   Responsive stack
 * </Stack>
 */
export const Stack = (props: StackProps) => {
  // Build direction classes using static map
  const directionClasses = getResponsiveStaticClass(props.direction, FLEX_DIRECTION_CLASSES);

  // Build align classes using static map
  const alignClasses =
    props.align !== undefined
      ? getResponsiveStaticClass(props.align, FLEX_ALIGN_CLASSES)
      : undefined;

  // Build justify classes using static map
  const justifyClasses =
    props.justify !== undefined
      ? getResponsiveStaticClass(props.justify, FLEX_JUSTIFY_CLASSES)
      : undefined;

  // Build wrap class
  const getWrapClass = (): string | undefined => {
    if (props.wrap === true) {
      return 'flex-wrap';
    }
    if (props.wrap === 'nowrap') {
      return 'flex-nowrap';
    }
    return undefined;
  };
  const wrapClass = getWrapClass();

  // Build inline styles using schema-based builder
  const gapValue = props.spacing !== undefined ? resolveSpacingValue(props.spacing) : undefined;

  const style: CSSProperties = {
    ...(gapValue !== undefined ? {gap: gapValue} : {}),
    ...buildStylesFromSchema(toRecord(props), [
      ...FLEX_ITEM_STYLE_MAPPINGS,
      ...SIZING_STYLE_MAPPINGS,
    ]),
  };

  const className = cn('flex', directionClasses, alignClasses, justifyClasses, wrapClass);

  return (
    <div
      role={props.role}
      className={className}
      style={Object.keys(style).length > 0 ? style : undefined}
      data-testid={suffixTestId('stack', props)}
    >
      {props.children}
    </div>
  );
};
